import React, { useState } from 'react';
import { X, CheckCircle, XCircle, Eye, AlertTriangle, Clock } from 'lucide-react';
import { BulkOperationResultsResponse, Driver, FleetSubtype } from '../../services/api';
import DriverViewModal from './DriverViewModal';

interface BulkCheckResultsModalProps {
    isOpen: boolean;
    onClose: () => void;
    results: BulkOperationResultsResponse | null;
    subtype?: FleetSubtype;
}

const BulkCheckResultsModal: React.FC<BulkCheckResultsModalProps> = ({ isOpen, onClose, results, subtype }) => {
    const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
    const [showDriverModal, setShowDriverModal] = useState(false);

    if (!isOpen || !results) return null;

    const { operation, items } = results.data;

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'completed':
                return (
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 border border-green-200">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Completed
                    </span>
                );
            case 'failed':
                return (
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 border border-red-200">
                        <XCircle className="w-3 h-3 mr-1" />
                        Failed
                    </span>
                );
            case 'processing':
                return (
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 border border-blue-200">
                        <Clock className="w-3 h-3 mr-1" />
                        Processing
                    </span>
                );
            default:
                return (
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 border border-gray-200">
                        {status}
                    </span>
                );
        }
    };

    const getRiskBadge = (risk?: string) => {
        if (!risk) return <span className="text-gray-400 text-xs">-</span>;

        switch (risk) {
            case 'high':
                return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 border border-red-200">High</span>;
            case 'medium':
                return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200">Medium</span>;
            case 'low':
                return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 border border-green-200">Low</span>;
            default:
                return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 border border-gray-200">{risk}</span>;
        }
    };

    const getLicenceValidity = (item: any) => {
        // Check if the result data contains validity information
        if (item.resultData?.valid === true) {
            return (
                <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 border border-green-200">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Valid
                </span>
            );
        } else if (item.resultData?.valid === false) {
            return (
                <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 border border-red-200">
                    <XCircle className="w-3 h-3 mr-1" />
                    Invalid
                </span>
            );
        } else if (item.status === 'failed') {
            return (
                <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 border border-gray-200">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Check Failed
                </span>
            );
        }
        return <span className="text-gray-400 text-xs">-</span>;
    };

    const handleViewDriver = (driver: Driver) => {
        setSelectedDriver(driver);
        setShowDriverModal(true);
    };

    return (
        <>
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                    {/* Header */}
                    <div className="flex justify-between items-start p-6 border-b border-gray-200">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">Bulk Licence Check Results</h2>
                            <p className="text-sm text-gray-600 mt-1">
                                Operation completed on {operation.completedAt ? new Date(operation.completedAt).toLocaleString('en-GB') : 'N/A'}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    {/* Summary Stats */}
                    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                        <div className="grid grid-cols-3 gap-4">
                            <div className="text-center">
                                <p className="text-2xl font-bold text-gray-900">{operation.totalItems}</p>
                                <p className="text-sm text-gray-600">Total Drivers</p>
                            </div>
                            <div className="text-center">
                                <p className="text-2xl font-bold text-green-600">{operation.successCount}</p>
                                <p className="text-sm text-gray-600">Successful</p>
                            </div>
                            <div className="text-center">
                                <p className="text-2xl font-bold text-red-600">{operation.failureCount}</p>
                                <p className="text-sm text-gray-600">Failed</p>
                            </div>
                        </div>
                    </div>

                    {/* Results Table */}
                    <div className="flex-1 overflow-auto p-6">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50 sticky top-0">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Driver
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Licence Number
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Validity
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Risk Level
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {items.map((item) => (
                                        <tr key={item.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {item.driver ? `${item.driver.firstName} ${item.driver.lastName}` : 'Unknown'}
                                                </div>
                                                {item.driver?.email && (
                                                    <div className="text-sm text-gray-500">{item.driver.email}</div>
                                                )}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <div className="text-sm font-mono text-gray-900">
                                                    {item.driver?.licenceNumber || '-'}
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                {getStatusBadge(item.status)}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                {getLicenceValidity(item)}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                {getRiskBadge(item.driver?.riskLevel)}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                {item.driver && (
                                                    <button
                                                        onClick={() => handleViewDriver(item.driver!)}
                                                        className="inline-flex items-center px-3 py-1 text-xs font-medium text-white rounded-md hover:opacity-90 transition-opacity"
                                                        style={{ backgroundColor: '#69BE28' }}
                                                    >
                                                        <Eye className="w-3 h-3 mr-1" />
                                                        View
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {items.length === 0 && (
                                <div className="text-center py-12">
                                    <p className="text-gray-500">No results found</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2"
                            style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>

            {/* Driver View Modal */}
            {selectedDriver && (
                <DriverViewModal
                    isOpen={showDriverModal}
                    onClose={() => {
                        setShowDriverModal(false);
                        setSelectedDriver(null);
                    }}
                    driver={selectedDriver}
                    subtype={subtype}
                />
            )}
        </>
    );
};

export default BulkCheckResultsModal;
